import { z } from 'zod';
import { ENTRY_LIMITS } from '../entry-kinds.js';
import { TOPIC_PLANNER_LIMITS } from '../ai/topic-planner.js';
import { KNOWLEDGE_CATEGORIES } from '../knowledge.js';
import { languageTagSchema } from '../language.js';
import { OCCASION_FIRE_SOURCES, OCCASION_LIMITS, OCCASION_TRIGGER_KINDS, occasionRefSchema } from './occasions.js';
import { PLANNING_LIMITS } from './planning-sessions.js';
/** The `data` schema of every inbox message kind (spec 110 §4.1); the kind table itself is in `inbox-kinds.ts`. */
const uuid = z.uuid();
const isoDate = z.iso.date();
/** `calendar_gap` (daily): a property's ISO week in the next 30 days has fewer non-cancelled publications than the target. */
export const calendarGapDataSchema = z
    .object({
    property_id: uuid,
    /** ISO week label, e.g. `2026-W41`. */
    iso_week: z.string().regex(/^\d{4}-W\d{2}$/),
    /** Monday of the week, tenant calendar day. */
    week_start: isoDate,
    planned_count: z.number().int().nonnegative(),
    target: z.number().int().positive(),
})
    .strict();
/** At most this many IDs travel in an `open_reviews` message. */
export const OPEN_REVIEWS_MAX_IDS = 20;
/** `open_reviews` (daily): content items waiting in `in_review`. */
export const openReviewsDataSchema = z
    .object({
    count: z.number().int().positive(),
    content_item_ids: z.array(uuid).max(OPEN_REVIEWS_MAX_IDS),
})
    .strict();
/**
 * `missing_media` (daily + module 50's `FindingResolvedListener`, AC-110-008):
 * a content item with an open missing-media check finding and at least one
 * `planned`, `scheduled` or `publishing` publication in the next 30 days.
 */
export const missingMediaDataSchema = z
    .object({
    property_id: uuid,
    content_item_id: uuid,
    /** The item's non-cancelled publications in the next 30 days. */
    publication_count: z.number().int().positive(),
    /** UTC time of the earliest of them. */
    next_scheduled_at: z.iso.datetime(),
})
    .strict();
/**
 * `publication_failed` (daily + module 60's `PublicationStatusListener`,
 * AC-110-009): a publication in status `failed`. `last_error_code` is module
 * 70's stable error code (never a platform message); the message offers only
 * `view` and `ignore` — a retry is the human's decision in the publication view.
 */
export const publicationFailedDataSchema = z
    .object({
    property_id: uuid,
    publication_id: uuid,
    content_item_id: uuid,
    scheduled_at: z.iso.datetime(),
    last_error_code: z.string().regex(/^[a-z][a-z0-9_.]{0,79}$/).nullable(),
})
    .strict();
/** A share in percent as a fixed two-decimal string, `"0.00"` .. `"100.00"` (exact, never a float). */
const shareString = z.string().regex(/^(100|\d{1,2})\.\d{2}$/);
const month = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
/** At most this many pillars travel in one content mix message (a property has far fewer). */
export const CONTENT_MIX_MAX_PILLARS = 50;
/**
 * One active pillar of a property in a content mix message: its target, the
 * actual share of the counted content items (per content item, not per
 * publication, AC-110-010) and whether the difference exceeds the tolerance.
 */
export const contentMixPillarSchema = z
    .object({
    pillar_id: uuid,
    target_share: shareString,
    actual_share: shareString,
    content_count: z.number().int().nonnegative(),
    outside_tolerance: z.boolean(),
})
    .strict();
/**
 * `content_mix_deviation` (weekly, AC-110-010): the distinct content items
 * with a non-cancelled publication in the next four weeks, per active pillar
 * of the property, when at least one pillar is outside the tolerance.
 */
export const contentMixDeviationDataSchema = z
    .object({
    property_id: uuid,
    period_start: isoDate,
    period_end: isoDate,
    tolerance_pp: shareString,
    total_count: z.number().int().positive(),
    /** Items without a pillar or with an archived one: counted in the total, in no pillar row. */
    unassigned_count: z.number().int().nonnegative(),
    pillars: z.array(contentMixPillarSchema).max(CONTENT_MIX_MAX_PILLARS),
})
    .strict();
/** `apartment_not_featured` (weekly, AC-110-011): never featured, or not for `threshold_days` or more. */
export const apartmentNotFeaturedDataSchema = z
    .object({
    property_id: uuid,
    apartment_id: uuid,
    /** Whole days since `last_featured_at`; `null` = never featured. */
    days_since: z.number().int().nonnegative().nullable(),
    last_featured_at: z.iso.datetime().nullable(),
    threshold_days: z.number().int().positive(),
})
    .strict();
/**
 * `entry_not_featured` (weekly, E470, spec 110 section 4.x): an active entry of a rotating type never featured, or
 * not within its type's `rotation_days`. Replaces `apartment_not_featured`, whose stored messages stay readable.
 */
export const entryNotFeaturedDataSchema = z
    .object({
    property_id: uuid,
    entry_id: uuid,
    /** The entry's name when the check ran, so the message reads even after a rename or deletion. */
    entry_name: z.string().min(1).max(ENTRY_LIMITS.nameMaxLength),
    /** Whole days since `last_featured_at`; `null` = never featured. */
    days_since: z.number().int().nonnegative().nullable(),
    last_featured_at: z.iso.datetime().nullable(),
    /** The type's singular name when the check ran (data the business entered, not display text of the app). */
    type_name: z.string().min(1).max(ENTRY_LIMITS.typeNameMaxLength),
    rotation_days: z.number().int().positive(),
})
    .strict();
/** How many days after today the daily routine warns about a desired date (AC-110-113). */
export const CONTENT_DUE_LOOKAHEAD_DAYS = 7;
/** `content_due_unapproved` (daily, AC-110-113): posts of a property with a close desired date and no approval, earliest first. */
export const contentDueUnapprovedDataSchema = z
    .object({
    property_id: uuid,
    count: z.number().int().positive(),
    content_item_ids: z.array(uuid).max(OPEN_REVIEWS_MAX_IDS),
    earliest_date: isoDate,
    period_start: isoDate,
    period_end: isoDate,
})
    .strict();
/** `content_mix_review` (monthly, AC-110-012): the mix of the past month's published content items per pillar. */
export const contentMixReviewDataSchema = z
    .object({
    property_id: uuid,
    /** The reviewed month, `YYYY-MM`. */
    month,
    period_start: isoDate,
    period_end: isoDate,
    tolerance_pp: shareString,
    total_count: z.number().int().nonnegative(),
    unassigned_count: z.number().int().nonnegative(),
    pillars: z.array(contentMixPillarSchema).max(CONTENT_MIX_MAX_PILLARS),
})
    .strict();
/** `strategy_review` (monthly in January, April, July, October; AC-110-012): suggests reviewing the theme grid. */
export const strategyReviewDataSchema = z
    .object({
    /** e.g. `2027-Q2`. */
    quarter: z.string().regex(/^\d{4}-Q[1-4]$/),
    quarter_start: isoDate,
})
    .strict();
/**
 * `seasonal_topic` (weekly, AC-110-036, S110-7): a theme slot of the yearly grid
 * that starts within the next four weeks and has no content item of its pillar
 * with a desired date in its period. `theme` is what the human wrote in the
 * grid (content, not UI text); the slot itself stays the source of truth for
 * `regenerate` and `create_campaign`, which re-read it.
 */
export const seasonalTopicDataSchema = z
    .object({
    property_id: uuid,
    theme_slot_id: uuid,
    theme: z.string().min(1).max(120),
    period_start: isoDate,
    period_end: isoDate,
    pillar_id: uuid,
})
    .strict();
/**
 * `topic_proposal`: an AI idea (content item in `idea`, the message's subject)
 * that a topic-planner run started from the inbox produced. `data` keeps the
 * property and the planned period, so `regenerate` can plan the same window
 * again; title and rationale live on the content item.
 */
export const topicProposalDataSchema = z
    .object({
    property_id: uuid,
    period_start: isoDate,
    period_end: isoDate,
    /** The occasion the idea was planned for (spec 110 section 4.8, S160-3). */
    occasion: occasionRefSchema.optional(),
    /** That occasion's planner theme, so `regenerate` plans the same occasion again. */
    theme: z.string().trim().min(1).max(TOPIC_PLANNER_LIMITS.maxThemeLength).optional(),
    /** Why the AI proposes this idea, from the run's output (S160-4, REQ-AUT-009: it names what it builds on). */
    rationale: z.string().trim().min(1).max(TOPIC_PLANNER_LIMITS.maxRationaleLength).optional(),
})
    .strict();
/** Limits of a research suggestion's text and source (spec 110 §4.4). */
export const RESEARCH_SUGGESTION_LIMITS = { titleMaxLength: 200, bodyMaxLength: 4000, sourceNameMaxLength: 200, sourceUrlMaxLength: 1000 };
/**
 * `research_suggestion` (spec 110 §4.4, REQ-110-014): external information with
 * its source, stored as a suggestion. The source name, an `https` URL and the
 * retrieval time are required; without them the message cannot be stored
 * (`source_required`). It never becomes knowledge by itself: `accept` creates a
 * `draft` knowledge item that a human verifies in module 20.
 */
export const researchSuggestionDataSchema = z
    .object({
    title: z.string().trim().min(1).max(RESEARCH_SUGGESTION_LIMITS.titleMaxLength),
    body: z.string().trim().min(1).max(RESEARCH_SUGGESTION_LIMITS.bodyMaxLength),
    language: languageTagSchema,
    proposed_category: z.enum(KNOWLEDGE_CATEGORIES),
    property_id: uuid,
    source_name: z.string().trim().min(1).max(RESEARCH_SUGGESTION_LIMITS.sourceNameMaxLength),
    source_url: z.url({ protocol: /^https$/ }).max(RESEARCH_SUGGESTION_LIMITS.sourceUrlMaxLength),
    retrieved_at: z.iso.datetime(),
})
    .strict();
/**
 * An account whose stored permissions lack an insights permission (spec 120
 * §4.1 step 3, AC-120-010): one open message per account, resolved when the
 * account is reconnected with them. The subject is the social account.
 */
export const analyticsPermissionMissingDataSchema = z
    .object({
    social_account_id: uuid,
    platform: z.enum(['instagram', 'facebook']),
    missing_permissions: z.array(z.string().regex(/^[a-z][a-z_]{0,79}$/)).max(10),
})
    .strict();
/**
 * `credits_low` (daily, spec 10 section 4.10, T140-1-5): a tenant billed in
 * credits has less than `CREDITS_LOW_PERCENT` of its granted credits left, or
 * none. One tenant-wide message, updated in place from low to exhausted and
 * resolved when credits are added. Severity `warning` while low, `critical`
 * when exhausted.
 */
export const creditsLowDataSchema = z
    .object({
    level: z.enum(['low', 'exhausted']),
    available: z.number().int().nonnegative(),
    granted: z.number().int().nonnegative().nullable(),
})
    .strict();
/** Posts a monthly report names at most (REQ-AUT-010: "die drei besten Beitraege"). */
export const MONTHLY_REPORT_TOP_POSTS = 3;
export const MONTHLY_REPORT_DIMENSIONS = ['pillar', 'format'];
/**
 * `monthly_report` (S160-4, REQ-AUT-010): module 120's plain-language report
 * of the past month, raised on the first days of the next month. `top` are
 * the best posts by interactions (reactions, comments, shares, saves), their
 * IDs also in `content_item_ids` so the detail resolves the titles; `finding`
 * is the clearest difference of the last 90 days between two pillars or
 * formats that the topic planner now leans on (`null` = too few posts).
 */
export const monthlyReportDataSchema = z
    .object({
    month,
    period_start: isoDate,
    period_end: isoDate,
    post_count: z.number().int().positive(),
    interactions: z.number().int().nonnegative(),
    content_item_ids: z.array(uuid).max(MONTHLY_REPORT_TOP_POSTS),
    top: z
        .array(z.object({ content_item_id: uuid, interactions: z.number().int().nonnegative(), reach: z.number().int().nonnegative().nullable() }).strict())
        .max(MONTHLY_REPORT_TOP_POSTS),
    finding: z
        .object({
        dimension: z.enum(MONTHLY_REPORT_DIMENSIONS),
        /** The property whose pillars are compared (pillars belong to one property); `null` for formats. */
        property_id: uuid.nullable(),
        /** Pillar ID or format key of the stronger and the weaker group. */
        higher: z.string().min(1).max(100),
        lower: z.string().min(1).max(100),
        /** `(higher - lower) / lower * 100` of the mean interactions per post, whole percent. */
        relative_difference: z.number().int().positive(),
        window_days: z.number().int().positive(),
    })
        .strict()
        .nullable(),
})
    .strict();
/**
 * `occasion_ready` (E300 S300-1, spec 110 section 4.9 rule 4): an occasion
 * post on standby was fired by the detection or by a person. The subject is
 * the content item; `event_date` is the occasion's tenant day. `source`
 * decides the wording: a detected weather occasion is announced, never
 * reported as having happened. Auto-resolved when the binding is dated or
 * cancelled.
 */
export const occasionReadyDataSchema = z
    .object({
    binding_id: uuid,
    trigger_kind: z.enum(OCCASION_TRIGGER_KINDS),
    label: z.string().min(1).max(OCCASION_LIMITS.labelMaxLength),
    event_date: isoDate,
    source: z.enum(OCCASION_FIRE_SOURCES),
    photo_needed: z.boolean(),
    /** `false` until the finalisation run succeeded or a member edited the text. */
    finalised: z.boolean(),
})
    .strict();
/**
 * `photo_needed` (E300 S300-3, spec 110 section 4.10 rule 6): a photo task of
 * an applied planning session. The subject is the campaign; `text` is the
 * planner's wording of what to photograph, `post_titles` the posts that wait
 * for it. Deduplicated per session and task key.
 */
export const photoNeededDataSchema = z
    .object({
    session_id: uuid,
    task_key: z.string().min(1).max(PLANNING_LIMITS.keyMaxLength),
    text: z.string().min(1).max(PLANNING_LIMITS.photoTaskTextMaxLength),
    post_titles: z.array(z.string().min(1).max(PLANNING_LIMITS.titleMaxLength)).max(PLANNING_LIMITS.maxPosts + PLANNING_LIMITS.maxOccasions),
})
    .strict();
//# sourceMappingURL=inbox-kind-data.js.map